import Button from '@/features/shared/Button';
import {
  MuiFormControl,
  MuiFormControlLabel,
  MuiFormLabel,
  MuiKeyboardDatePicker,
  MuiPickersUtilsProvider,
  MuiRadio,
  MuiTextField,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import StyledCheckbox from '@/features/shared/StyledCheckbox';
import DateFnsUtils from '@date-io/date-fns';
import clsx from 'clsx';
import { format } from 'date-fns';
import React, { memo, useCallback } from 'react';
import MarketoOptions from './MarketoOptions';
import MarketoRow from './MarketoRow';
import MarketoSelectControl from '@/features/MarketoForm/Controls/SelectControl/SelectControl';

const MarketoControl = ({
  type,
  label,
  value,
  html,
  controls,
  legend,
  options,
  attributes,
  checked,
  disabled,
  classes,
  onChange,
  error,
  theme,
  isSingleColumn,
  marketoInstance,
}: any) => {
  const selectIconClassName = clsx(classes.selectIcon, `${theme}-theme`);
  const radioOrCheckboxClassName = clsx(classes.radioOrCheckbox, `${theme}-theme`);
  const handleTargetValueChange = useCallback(
    ({ target }: any) => {
      const { value: inputValue, name, id } = target;
      onChange({ value: inputValue, name, id, target });
    },
    [onChange],
  );
  const handleValueChange = useCallback(
    ({ target }: any, nextValue: any) => {
      const { value: inputValue } = nextValue || { value: '' };
      onChange({ value: inputValue, name: attributes?.name, id: attributes?.id, target });
    },
    [attributes?.id, attributes?.name, onChange],
  );
  const handleDateChange = React.useCallback(
    (nextDate: Date | null = null) => {
      const inputValue = nextDate ? format(nextDate, 'yyyy-MM-dd') : null;
      onChange({ value: inputValue, name: attributes?.name, id: attributes?.id, target: {} });
    },
    [attributes?.id, attributes?.name, onChange],
  );

  const handleNotYouClick = React.useCallback(
    (e: React.SyntheticEvent<MouseEvent>) => {
      e.preventDefault();
      const element = marketoInstance?.getFormElem()?.[0]?.querySelector('.mktoNotYou');
      element?.click();
    },
    [marketoInstance],
  );

  switch (type) {
    case 'notYou':
      return <CaptureClicksRichText theme={theme} content={html} onClick={handleNotYouClick} />;
    case 'html':
      return <CaptureClicksRichText theme={theme} content={html} />;
    case 'fieldset':
      return (
        <MuiFormControl component="fieldset" className={classes.formControl}>
          {legend && (
            <MuiFormLabel component="legend" className={classes.legend}>
              {legend}
            </MuiFormLabel>
          )}
          <MarketoRow
            theme={theme}
            controls={controls}
            onChange={onChange}
            spacing={0}
            value={value}
            classes={classes}
          />
        </MuiFormControl>
      );
    case 'options':
      return (
        <MarketoOptions
          options={options}
          theme={theme}
          onChange={handleTargetValueChange}
          classes={classes}
          MarketoControlFn={MarketoControl}
          error={error}
        />
      );
    case 'select':
      return (
        <MarketoSelectControl
          classes={classes}
          error={error}
          disabled={disabled}
          attributes={attributes}
          label={label}
          value={value}
          onChange={handleTargetValueChange}
          onChangeAutocomplete={handleValueChange}
          selectIconClassName={selectIconClassName}
          options={options}
        />
      );
    case 'text':
    case 'email':
    case 'tel':
    case 'textarea':
    case 'url':
      return (
        <MuiTextField
          type="text"
          label={label}
          variant="outlined"
          size="small"
          fullWidth
          multiline={/textarea/.test(type)}
          value={value?.[attributes?.name]}
          onChange={handleTargetValueChange}
          error={Boolean(error) || /true/.test(attributes?.['aria-invalid'])}
          helperText={error || ''}
          className={classes.textField}
          inputProps={attributes}
          disabled={disabled}
        />
      );
    case 'date':
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <MuiKeyboardDatePicker
            className={clsx(
              classes.datePicker,
              (Boolean(error) || /true/.test(attributes?.['aria-invalid'])) && 'error',
            )}
            label={label}
            format="MM/dd/yyyy"
            value={value?.[attributes?.name] ? `${value?.[attributes?.name]} 00:00:00` : null}
            onChange={handleDateChange}
            KeyboardButtonProps={attributes}
          />
        </MuiPickersUtilsProvider>
      );
    case 'checkbox':
      return (
        <MuiFormControlLabel
          control={
            <StyledCheckbox
              theme={theme}
              checked={checked}
              disabled={disabled}
              value={value}
              inputProps={attributes}
              onChange={handleTargetValueChange}
              className={radioOrCheckboxClassName}
              error={error}
            />
          }
          labelPlacement={label.pre ? 'start' : 'end'}
          label={
            <CaptureClicksRichText
              theme={theme}
              content={label.pre || label.post}
              className={clsx(!!error && 'Mui-error-text')}
            />
          }
        />
      );
    case 'radio':
      return (
        <MuiFormControlLabel
          control={
            <MuiRadio
              checked={checked}
              disabled={disabled}
              value={value}
              inputProps={attributes}
              onChange={handleTargetValueChange}
              className={radioOrCheckboxClassName}
            />
          }
          labelPlacement={label?.pre ? 'start' : 'end'}
          label={
            <CaptureClicksRichText
              theme={theme}
              content={label?.pre || label?.post}
              className={clsx(!!error && 'Mui-error-text')}
            />
          }
        />
      );
    case 'submit':
    case 'button':
      return (
        <Button
          fullWidth={isSingleColumn}
          theme={theme}
          disabled={disabled}
          type={type}
          {...attributes}
          className={classes.submit}
          data-datalayer-ignore-cta
        >
          {label}
        </Button>
      );
    default:
      return null;
  }
};

export default memo(MarketoControl);
